/**
 * Compensation categories for different types of adjustments
 */
export type CompensationCategory =
  // Compensation categories
  | 'bonus'
  | 'monthly_salary'
  | 'mileage_allowance'
  | 'equipment_allowance'
  | 'training_compensation'
  | 'other_compensation'
  // Deduction categories
  | 'advance_payment'
  | 'equipment_rental'
  | 'insurance'
  | 'parking_fees'
  | 'fines'
  | 'other_deduction'

/**
 * Where the compensation/deduction should be applied in the salary calculation
 */
export type CompensationApplicationTarget = 'net_earnings' | 'gross_salary' | 'net_salary'

/**
 * Type of adjustment - compensation adds money, deduction removes money
 */
export type CompensationType = 'compensation' | 'deduction'

/**
 * Target type for compensation - applies to driver or vehicle
 */
export type CompensationTargetType = 'driver' | 'vehicle'

/**
 * Main compensation interface
 */
export interface Compensation {
  id: string
  driverId: string                              // Reference to driver (always populated, even for vehicle compensations)
  vehicleId?: string                            // Optional reference to vehicle (when compensation is for a vehicle)
  period: string                                // Monthly period in format "yyyy-mm-01" (e.g., "2025-08-01")
  type: CompensationType                        // compensation or deduction
  applyTo: CompensationApplicationTarget        // where to apply in calculation
  amount: number                                // always positive, type determines add/subtract
  category: CompensationCategory               // predefined category
  comment?: string                             // optional description
  isDeleted?: boolean                          // soft delete flag
  createdAt: Date
  updatedAt: Date
}

/**
 * Form/creation types
 */
export interface CreateCompensationData {
  driverId?: string                             // Optional when vehicleId is provided
  vehicleId?: string                            // Optional vehicle reference
  period: string
  type: CompensationType
  applyTo: CompensationApplicationTarget
  amount: number
  category: CompensationCategory
  comment?: string
}

export interface UpdateCompensationData {
  type?: CompensationType
  applyTo?: CompensationApplicationTarget
  amount?: number
  category?: CompensationCategory
  comment?: string
}

/**
 * Validation error type
 */
export interface CompensationValidationError {
  field: string
  message: string
}

/**
 * Category display information
 */
export interface CompensationCategoryInfo {
  value: CompensationCategory
  label: string
  type: CompensationType
}

/**
 * Predefined category options with Swedish labels
 */
export const COMPENSATION_CATEGORIES: CompensationCategoryInfo[] = [
  // Compensations
  { value: 'bonus', label: 'Bonus', type: 'compensation' },
  { value: 'monthly_salary', label: 'Månadslön', type: 'compensation' },
  { value: 'mileage_allowance', label: 'Milersättning', type: 'compensation' },
  { value: 'equipment_allowance', label: 'Utrustningsbidrag', type: 'compensation' },
  { value: 'training_compensation', label: 'Utbildningsersättning', type: 'compensation' },
  { value: 'other_compensation', label: 'Annan ersättning', type: 'compensation' },

  // Deductions
  { value: 'advance_payment', label: 'Förskottsbetalning', type: 'deduction' },
  { value: 'equipment_rental', label: 'Utrustningshyra', type: 'deduction' },
  { value: 'insurance', label: 'Försäkring', type: 'deduction' },
  { value: 'parking_fees', label: 'Parkeringsavgifter', type: 'deduction' },
  { value: 'fines', label: 'Böter', type: 'deduction' },
  { value: 'other_deduction', label: 'Annat avdrag', type: 'deduction' }
]

/**
 * Application target options with Swedish labels
 */
export const COMPENSATION_APPLICATION_TARGETS: { value: CompensationApplicationTarget; label: string; description: string }[] = [
  {
    value: 'net_earnings',
    label: 'Nettointäkter',
    description: 'Appliceras före moms- och provisionsberäkning'
  },
  {
    value: 'gross_salary',
    label: 'Bruttolön',
    description: 'Appliceras på slutlig bruttolön'
  },
  {
    value: 'net_salary',
    label: 'Lön efter skatt',
    description: 'Appliceras på lönen efter skatteavdrag'
  }
]

/**
 * Get category info by value
 */
export function getCompensationCategoryInfo(category: CompensationCategory): CompensationCategoryInfo {
  const info = COMPENSATION_CATEGORIES.find(c => c.value === category)
  if (!info) {
    throw new Error(`Unknown compensation category: ${category}`)
  }
  return info
}

/**
 * Get categories filtered by type
 */
export function getCompensationCategoriesByType(type: CompensationType): CompensationCategoryInfo[] {
  return COMPENSATION_CATEGORIES.filter(c => c.type === type)
}

/**
 * Bulk compensation row for table editing
 */
export interface BulkCompensationRow {
  driverId?: string
  vehicleId?: string
  driverName?: string
  vehicleName?: string
  targetType: CompensationTargetType
  type?: CompensationType
  applyTo?: CompensationApplicationTarget
  amount?: number
  category?: CompensationCategory
  comment?: string
}

/**
 * Bulk compensation data for creation
 */
export interface BulkCompensationData {
  period: string
  compensations: Array<{
    driverId?: string
    vehicleId?: string
    type: CompensationType
    applyTo: CompensationApplicationTarget
    amount: number
    category: CompensationCategory
    comment?: string
  }>
}